import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client';
import { format } from 'date-fns';
import { decode } from 'he';
import { Trash2, Plus, Columns2 } from 'lucide-react';
import DOMPurify from 'dompurify';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { plainText as toPlainText } from '../../lib/plainText';
import type { Editor } from '@tiptap/react';
import type { Node as PmNode } from '@tiptap/pm/model';
import { ArticleBodyEditor } from '../content/ArticleBodyEditor';
import { CopyEditPopover } from '../content/CopyEditPopover';
import { stillInDraft, type CopyEditField, type DraftTexts } from '../content/copyEditQuote';
import type { Finding } from '../cms/shared';
import { VersionCompareView } from '../content/VersionCompare';
import { HistoryView } from '../cms/HistoryView';
import { writeKeyRows } from '../content/writeKeyHistory';
import { resolveDisplayMediaUrl } from '../../utils/mediaUrl';
import { TopicMediaPanel } from '../content/TopicMediaPanel';
import { MediaUrlField } from '../content/MediaUrlField';
import {
  GET_CONTENT_ITEM,
  GET_ACCOUNT_TAXONOMY,
  GET_ACCOUNT_TOPIC_FIELDS,
  GET_TOPIC_VERSIONS,
  SAVE_CONTENT_ITEM,
  RESTORE_TOPIC_VERSION,
  DELETE_TINYCMS_ARTICLE,
  ATOMIZE_TOPIC,
  SUMMARIZE_CONTENT_DRAFT,
  GET_TINYCMS_ARTICLE,
  GET_TINYCMS_SITE,
  UPDATE_TINYCMS_ARTICLE,
  ADOPT_TINYCMS_ARTICLE,
  UNDO_ADOPT_TINYCMS_ARTICLE,
  AUTOSAVE_TINYCMS_ARTICLE,
  GET_COWORK_MCP_CONNECTOR,
  CREATE_TINYCMS_ARTICLE,
  RUN_TINYCMS_COPY_EDIT,
  RUN_TINYCMS_DRAFT_COPY_EDIT,
  PUBLISH_TOPIC,
  UNPUBLISH_TOPIC,
} from '../../graphql';
import {
  GetContentItemResponse,
  GetAccountTaxonomyResponse,
  GetAccountTopicFieldsResponse,
  GetTopicVersionsResponse,
  ProvenanceTaxonomies,
  VerifiedFact,
  NamedEntity,
  TopicVersion,
  TopicVersionSnapshot,
  SaveContentItemInput,
  AccountTopicField,
} from '../../types/graphql';
import { useAccount } from '../../context/AccountContext';
import { ArticlePreview } from '../cms/CmsPages';
import { ContentUnavailable } from '../content/ContentUnavailable';
import { DuplicateCard } from '../content/DuplicateCard';
import { AskAgent } from '../content/AskAgent';
import { Loader } from '../common/PageLoader';
import { primaryCategory } from '../cms/categories';
import { useSeesExtractedDetail } from '../settings/supportAccess';
import { FactsEntities } from './FactsEntities';
import { BylineEditor, BylineNames, structuredDataLine, useSiteAuthors } from './ArticleByline';
import { useAddons } from '../cms/addons';
import { useTinycmsSites } from '../cms/sites';
import { FEED_BRING_IN } from '../cms/FeedBringIn';
import { bringOneNote, type BringInStatus } from '../cms/feedBringInWords';
import { showInkToast } from '../cms/InkToast';
import { useCopy } from '../../lib/copyToClipboard';
import { ArticleReachCard } from '../reach/ArticleReachCard';
import { hasWriting, newArticleInput, slugFromTitle } from '../content/newArticle';
import { AgentFirstButton } from '../cms/AskYourAgent';
import { ARTICLE_SETTINGS_PATH } from '../cms/manageAreas';
import {
  ArticleChecks,
  articleChecks,
  CmsPublishCard,
  nextFinding,
  sameFinding,
  DatasetCard,
  SearchSharing,
  TagEditor,
  isHttpUrl,
  isValidSlug,
  type CmsArticle,
  type SeoDraft,
} from '../content/ArticleWorkflow';
import { askInk } from '../cms/InkConfirm';
import { agentOwnName, plainBodyHtml } from '../approvals/approvalWords';
import { ARTICLE_STATUS } from '../statusWords';

type AutosaveStatus = 'idle' | 'dirty' | 'saving' | 'saved' | 'error';

const inputClass =
  'w-full px-3 py-2 bg-form-dark text-cmp-ink rounded border border-cmp-line-2 focus:outline-none focus:ring-2 focus:ring-primary';

// Source-type vocabulary the atomizer emits (see ATOMIZATION-METHODOLOGY §7).
// Values are the canonical slugs; labels mirror the product wireframe.
const SOURCE_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'stated_in_article', label: 'Stated in article' },
  { value: 'attributed_fact', label: 'Attributed fact' },
  { value: 'disputed_claim', label: 'Disputed claim' },
  { value: 'direct_quote', label: 'Direct quote' },
  { value: 'data_cited', label: 'Data / statistic cited' },
  { value: 'official_record', label: 'Official record referenced' },
];

const sourceTypeLabel = (value?: string): string =>
  SOURCE_TYPE_OPTIONS.find((o) => o.value === value)?.label || value || '';

function FieldLabel({ children }: { children: ReactNode }) {
  return (
    <label className="block font-cmp-mono text-[0.66rem] tracking-[0.14em] uppercase text-cmp-ink-3 mb-1.5">
      {children}
    </label>
  );
}

interface EditableFields {
  title: string;
  summary: string;
  bodyText: string;
  mediaUrl: string;
  destinationUrl: string;
  category: string;
  keywords: string;
  contentType: string;
}

const emptyFields: EditableFields = {
  title: '',
  summary: '',
  bodyText: '',
  mediaUrl: '',
  destinationUrl: '',
  category: '',
  keywords: '',
  contentType: 'webpage',
};

/**
 * The tinycms sidecar half of the working copy (slug, SEO, hero alt, author,
 * tags). It saves through updateTinycmsArticle when editing finishes rather
 * than on every autosave, since each of those writes records a version.
 */
interface CmsFields extends SeoDraft {
  heroAlt: string;
  author: string;
  /** The byline: author ids, the author first, then co-authors (Prototype v17). */
  authorIds: string[];
  showUpdated: boolean;
  tags: string[];
}

const emptyCmsFields: CmsFields = {
  slug: '',
  section: '',
  metaTitle: '',
  metaDescription: '',
  canonicalUrl: '',
  ogImageUrl: '',
  heroAlt: '',
  author: '',
  authorIds: [],
  showUpdated: false,
  tags: [],
};

function cmsFieldsFrom(article?: CmsArticle | null): CmsFields {
  const c = article?.content;
  if (!c) return emptyCmsFields;
  return {
    slug: c.slug || article?.slug || '',
    section: c.section || '',
    metaTitle: c.seo?.metaTitle || '',
    metaDescription: c.seo?.metaDescription || '',
    canonicalUrl: c.seo?.canonicalUrl || '',
    ogImageUrl: c.seo?.ogImageUrl || '',
    heroAlt: c.hero?.alt || '',
    author: c.author || '',
    authorIds: c.authorIds || [],
    showUpdated: Boolean(c.showUpdated),
    tags: c.tags || [],
  };
}

/** Only what changed, in updateTinycmsArticle's shape. */
function cmsPatch(before: CmsFields, after: CmsFields) {
  const patch: Record<string, unknown> = {};
  if (after.slug !== before.slug) patch.slug = after.slug;
  if (after.section !== before.section) patch.section = after.section || null;
  if (after.heroAlt !== before.heroAlt) patch.hero = { alt: after.heroAlt.trim() || null };
  if (after.author !== before.author) patch.author = after.author.trim() || null;
  if (after.authorIds.join(',') !== before.authorIds.join(',')) patch.authors = after.authorIds;
  if (after.showUpdated !== before.showUpdated) patch.showUpdated = after.showUpdated;
  if (after.tags.join('\u0000') !== before.tags.join('\u0000')) patch.tags = after.tags;
  const seo: Record<string, unknown> = {};
  for (const k of ['metaTitle', 'metaDescription'] as const) {
    if (after[k] !== before[k]) seo[k] = after[k].trim() || null;
  }
  for (const k of ['canonicalUrl', 'ogImageUrl'] as const) {
    if (after[k] !== before[k]) seo[k] = after[k] || null;
  }
  if (Object.keys(seo).length) patch.seo = seo;
  return patch;
}

/** Why the sidecar fields can't save yet, or '' when they can. */
function cmsFieldsProblem(f: CmsFields): string {
  if (!isValidSlug(f.slug)) {
    return 'The URL slug must be lowercase letters and digits separated by single hyphens.';
  }
  if (f.canonicalUrl && !isHttpUrl(f.canonicalUrl))
    return 'The canonical URL must start with https://.';
  if (f.ogImageUrl && !isHttpUrl(f.ogImageUrl))
    return 'The share image URL must start with https://.';
  return '';
}

function publishStatusLabel(status?: string | null) {
  // "Live", as the Content list says (UX review round 6).
  if (status === 'published') return ARTICLE_STATUS.live;
  if (status === 'scheduled') return ARTICLE_STATUS.scheduled;
  return ARTICLE_STATUS.draft;
}

function topicLabel(value?: string | null) {
  const raw = primaryCategory(value);
  if (/^[a-z0-9]{1,8}$/i.test(raw)) return raw.toUpperCase();
  return raw;
}

function dimensionLabel(key: string) {
  const words = key.replace(/[_-]+/g, ' ').trim();
  if (!words) return key;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function statusColor(status?: string | null) {
  if (status === 'published') return 'var(--ink-green)';
  if (status === 'scheduled') return 'var(--ink-accent)';
  return 'var(--ink-ink3)';
}

const STREAM_PLAYER = /^https:\/\/customer-[a-z0-9]+\.cloudflarestream\.com\/[a-f0-9]{32}\/iframe/i;

// Scraped/edited HTML is untrusted and can be double entity-encoded, so decode
// until stable, then sanitize before rendering or persisting.
function toSafeHtml(raw?: string | null): string {
  if (!raw) return '';
  let decoded = raw;
  for (let i = 0; i < 3; i++) {
    const next = decode(decoded);
    if (next === decoded) break;
    decoded = next;
  }
  const clean = DOMPurify.sanitize(decoded, {
    USE_PROFILES: { html: true },
    ADD_TAGS: ['iframe'],
    ADD_ATTR: ['allow', 'allowfullscreen', 'loading'],
  });
  if (!/<iframe/i.test(clean)) return clean;
  // The only frame kept is the site's own video player (as on the public site).
  const t = document.createElement('template');
  t.innerHTML = clean;
  t.content.querySelectorAll('iframe').forEach((f) => {
    if (!STREAM_PLAYER.test(f.getAttribute('src') || '')) f.remove();
  });
  return t.innerHTML;
}

/**
 * Count entries in topics.media_items, which is polymorphic by ingest path:
 * RSS writes { images: [], videos: [] }, while WordPress and the media layer
 * write a flat array of { role, url, … }. Reading one shape blindly reports 0
 * for content ingested through the other.
 */
function countMediaItems(mediaItems: unknown, kind: 'images' | 'videos'): number {
  if (!mediaItems) return 0;
  if (Array.isArray(mediaItems)) {
    // Flat form carries no video entries today; everything linked is an image.
    return kind === 'images' ? mediaItems.length : 0;
  }
  const bag = mediaItems as { images?: unknown[]; videos?: unknown[] };
  return (kind === 'images' ? bag.images?.length : bag.videos?.length) ?? 0;
}

/** What a write key did to an article that isn't in tinycms yet (writeKeyHistory.ts). */
function WriteKeyHistory({ versions }: { versions: TopicVersion[] }) {
  const rows = writeKeyRows(versions);
  if (!rows.length) return null;
  return (
    <section data-part="write-key-history" className="ink-rail-block">
      <h3 className="ink-rail-label">History</h3>
      <ol className="wk-history">
        {rows.map((r) => (
          <li key={r.v}>
            <span>
              v{r.v} · {r.what}
            </span>
            <span className="ink-muted">{[r.who, r.when].filter(Boolean).join(' · ')}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}

export function ContentDetail({ isNew = false }: { isNew?: boolean } = {}) {
  const navigate = useNavigate();
  const { id: topicId } = useParams<{ id: string }>();
  // New article (UX optimization review, 2026-10-24): /content/new is this
  // editor with nothing saved. Nothing is created until Create draft, so
  // Cancel, or leaving for any other page, leaves nothing behind.

  const { accountId, accounts, selectAccountById, isLoading: accountsLoading } = useAccount();
  // Atomize (facts, entities, summarize) is an add-on. Without it the article
  // still opens and edits; its actions are hidden, and so are the facts and
  // entities it produces unless the site holds it (switched off still shows them).
  const { atomizeOn, atomizeHeld, reachOn } = useAddons();
  // Facts & entities and the Prism AI block are TinyCMS technical support's
  // only (supportAccess.tsx); the API sends nobody else any facts anyway.
  const seesExtracted = useSeesExtractedDetail(accountId);
  const { sites } = useTinycmsSites();
  const siteUrl = sites.find((s) => s.accountId === accountId)?.siteUrl || '';

  const {
    data,
    loading,
    error: itemError,
    refetch,
  } = useQuery<GetContentItemResponse>(GET_CONTENT_ITEM, {
    variables: { id: topicId },
    skip: !topicId,
  });
  const { data: taxData } = useQuery<GetAccountTaxonomyResponse>(GET_ACCOUNT_TAXONOMY, {
    variables: { accountId },
    skip: !accountId,
  });
  const { data: topicFieldsData } = useQuery<GetAccountTopicFieldsResponse>(
    GET_ACCOUNT_TOPIC_FIELDS,
    { variables: { accountId }, skip: !accountId }
  );
  const {
    data: versionsData,
    loading: versionsLoading,
    refetch: refetchVersions,
  } = useQuery<GetTopicVersionsResponse>(GET_TOPIC_VERSIONS, {
    variables: { topicId },
    skip: !topicId,
  });

  // The tinycms view of the same topic: working copy vs live, its hash, checks,
  // SEO. Null for a topic not brought under tinycms (the server answers
  // NOT_FOUND), which keeps the older publish controls below.
  const {
    data: cmsData,
    loading: cmsLoading,
    refetch: refetchCms,
  } = useQuery<{ tinycmsArticle: CmsArticle | null }>(GET_TINYCMS_ARTICLE, {
    variables: { accountId, id: topicId },
    skip: !accountId || !topicId,
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
  });
  // Where the site puts articles (its article address and collections), for
  // the address shown under Search & sharing and its Lives under choice.
  const { data: siteDocData } = useQuery<{ tinycmsSite: any }>(GET_TINYCMS_SITE, {
    variables: { accountId },
    skip: !accountId,
    errorPolicy: 'all',
  });
  const articleAddressing = useMemo(() => {
    const working = siteDocData?.tinycmsSite?.working || {};
    return {
      base:
        typeof working.seo?.articlesPath === 'string' && working.seo.articlesPath
          ? working.seo.articlesPath
          : 'article',
      sections: (Array.isArray(working.collections) ? working.collections : [])
        .filter((c: any) => c?.slug)
        .map((c: any) => ({ slug: String(c.slug), name: String(c.name || c.slug) })),
    };
  }, [siteDocData]);
  // The unsaved article stands in as a tinycms draft, so the editor shows the
  // same fields (byline, alt text, search & sharing) it shows for any article.
  const [newNoindex, setNewNoindex] = useState(false);
  const newCms = useMemo<CmsArticle | null>(
    () =>
      isNew
        ? {
            id: 'new',
            slug: '',
            title: '',
            workingHash: '',
            state: { live: null, scheduled: null, hasUnpublishedChanges: false },
            content: {
              title: '',
              slug: '',
              summary: null,
              hero: null,
              author: null,
              authorIds: [],
              tags: [],
              seo: {
                metaTitle: null,
                metaDescription: null,
                canonicalUrl: null,
                ogImageUrl: null,
                noindex: newNoindex,
              },
            },
            dataset: null,
          }
        : null,
    [isNew, newNoindex]
  );
  const cms = newCms || cmsData?.tinycmsArticle || null;
  // Not in TinyCMS yet (from a feed, or made before approvals): read-only until
  // it's brought in — no Edit, no E, no double-click to edit (QA round 13).
  const awaitingBringIn = !cms && !cmsLoading;
  const [updateCms, { loading: updatingCms }] = useMutation(UPDATE_TINYCMS_ARTICLE);
  const [adoptCms, { loading: adopting }] = useMutation(ADOPT_TINYCMS_ARTICLE);
  const [undoAdoptCms] = useMutation(UNDO_ADOPT_TINYCMS_ARTICLE);
  /**
   * An article from a feed isn't a tinycms article yet, so it has no Checks,
   * Search & sharing, byline or approvals. "Bring into TinyCMS" (Feed Articles
   * design, QA round 12) makes it one: a live one stays live as version 1,
   * approved by this member; a draft stays a draft. The confirm is the app's
   * own: window.confirm is silently refused in some browsers, which left the
   * button doing nothing (QA round 12).
   */
  const [bringAsk, setBringAsk] = useState(false);
  // A live article that isn't in tinycms yet can still come off the site, with
  // Undo putting it back under its own date (backlog b4e3ef5a).
  const [unpublishTopic, { loading: unpublishing }] = useMutation(UNPUBLISH_TOPIC);
  const [republishTopic] = useMutation(PUBLISH_TOPIC);
  // Whether readers see an article that isn't in TinyCMS: 'row' when it's
  // published, 'revision' when only approved versions show. Bringing one in
  // keeps it live as version 1 only in the first case (adoptTopic's rule).
  const { data: liveSourceData } = useQuery<{ tinycmsSettings: { liveSource?: string } }>(
    TINYCMS_LIVE_SOURCE,
    { variables: { accountId }, skip: !accountId, errorPolicy: 'all' }
  );
  const readersSeeRow = (liveSourceData?.tinycmsSettings?.liveSource ?? 'row') === 'row';
  // The author the feed sent with it, the name bring-in credits (the server's
  // feedClassification bylineName): "By Jane Doe", never an address.
  // The feed's alt text for its lead image (feedClassification heroAlt): the
  // hero's Alt chip before it's brought in.
  const feedHeroAlt = useMemo(() => {
    const t = data?.topic;
    const hero = String(t?.primaryMediaUrl || t?.mediaUrl || '').trim();
    const mi = t?.mediaItems as {
      images?: Array<{ url?: string; alt?: string; caption?: string }>;
    } | null;
    const list = Array.isArray(mi) ? mi : (mi?.images ?? []);
    const img = hero ? list.find((x) => String(x?.url || '').trim() === hero) : null;
    return String(img?.alt || img?.caption || '').trim() || null;
  }, [data]);
  const feedAuthor = useMemo(() => {
    const cf = (data?.topic?.customFields || {}) as {
      _wp?: { author?: unknown };
      _feed?: { author?: unknown };
    };
    for (const raw of [cf._wp?.author, cf._feed?.author]) {
      let name = typeof raw === 'string' ? raw.replace(/\s+/g, ' ').trim() : '';
      const inParens = name.match(/^\S+@\S+\s*\((.+)\)$/);
      if (inParens) name = inParens[1].trim();
      name = name.replace(/^by\s+/i, '').trim();
      if (name && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(name) && name.length <= 120) return name;
    }
    return null;
  }, [data]);
  // "2,514 articles from this feed · Bring all in" (Feed Articles design A):
  // Content's banner and confirm do the rest.
  const { data: feedData } = useQuery<{ feedBringIn: BringInStatus }>(FEED_BRING_IN, {
    variables: { accountId },
    skip: !accountId || Boolean(cms) || cmsLoading,
    errorPolicy: 'all',
  });
  const bringIn = async ({ quiet = false } = {}) => {
    if (!accountId || !topicId) return;
    try {
      const { data: adopted } = await adoptCms({ variables: { accountId, topicId } });
      setBringAsk(false);
      await refreshAll();
      const workingHash: string | undefined = adopted?.adoptTinycmsArticle?.workingHash;
      // The design's toast, with Undo while nothing has changed (QA round 13).
      if (!quiet)
        showInkToast(
          'It’s in tinycms. Edits wait for your approval.',
          workingHash
            ? {
                label: 'Undo',
                onClick: () => {
                  void undoAdoptCms({ variables: { accountId, topicId, workingHash } })
                    .then(() => refreshAll())
                    .then(() => showInkToast('It’s back the way your feed sent it.'))
                    .catch((err) =>
                      showInkToast(err instanceof Error ? err.message : 'That couldn’t be undone.')
                    );
                },
              }
            : undefined
        );
    } catch (err) {
      setBringAsk(false);
      if (!quiet)
        setMessage(`Failed: ${err instanceof Error ? err.message : 'It couldn’t be brought in'}`);
    }
  };
  const [autosaveCms] = useMutation(AUTOSAVE_TINYCMS_ARTICLE);

  const newItem = useMemo(
    () =>
      isNew
        ? ({
            id: 'new',
            accountId,
            title: '',
            contentType: 'article',
            publishStatus: 'draft',
          } as unknown as NonNullable<GetContentItemResponse['topic']>)
        : null,
    [isNew, accountId]
  );
  const item = isNew ? newItem : data?.topic;

  // A link can open another site's article (any site you belong to). Its
  // tinycms state, fields and media all live on that site, so follow it there
  // once. The site menu's own switch leaves an open article for its list
  // first (siteSwitchPath.ts), so this never fights it.
  const ownerId = item?.accountId || null;
  const ownerSite = ownerId ? accounts.find((a) => a.id === ownerId) : undefined;
  const otherSite = Boolean(ownerSite && ownerId !== accountId);
  const followedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!otherSite || !ownerSite || followedRef.current === item?.id) return;
    // The person just switched sites from the menu: the address has already
    // moved to the list, though this screen hasn't unmounted yet. Following
    // the article now would switch them straight back.
    if (!window.location.pathname.replace(/\/+$/, '').endsWith(`/content/${topicId}`)) return;
    followedRef.current = item?.id ?? null;
    selectAccountById(ownerSite.id);
    showInkToast(`Switched to ${ownerSite.name}, where this article lives.`);
  }, [otherSite, ownerSite, item?.id, selectAccountById, topicId]);
  const dimensions = useMemo(() => taxData?.accountTaxonomy || [], [taxData]);
  const topicFieldDefs = useMemo(
    () => (topicFieldsData?.accountTopicFields || []).filter((f) => f.isActive !== false),
    [topicFieldsData]
  );
  const versions = versionsData?.topicVersions || [];
  // The version waiting for approval is the agent's when nobody saved it in the
  // app (an MCP save records no person). Then the panel asks for approval and a
  // status line above the article says who is asking (Prototype v7).
  const latestVersion = versions.length
    ? versions.reduce((a, b) => (b.versionNumber > a.versionNumber ? b : a))
    : null;
  const agentMade = Boolean(
    cms &&
      cms.state.hasUnpublishedChanges &&
      !cms.state.scheduled &&
      latestVersion &&
      !latestVersion.createdBy
  );
  const { data: connectorData } = useQuery<{
    coworkMcpConnector: { label?: string | null } | null;
  }>(GET_COWORK_MCP_CONNECTOR, { variables: { accountId }, skip: !accountId });
  // The product's name or a default label isn't the agent's (feedback 9f70af88).
  const agentName = agentOwnName(connectorData?.coworkMcpConnector?.label) || 'Your agent';
  // The banner over a waiting version names who proposed it: the agent that
  // changed it, or the feed it came in from.
  // The panel's last line, "Source: Monte Carlo Cowork" (Prototype): the feed
  // or connector it came through; else who wrote its first version — the agent
  // when nobody signed it (an MCP save records no person), else that person.
  const firstVersion = versions.length
    ? versions.reduce((a, b) => (b.versionNumber < a.versionNumber ? b : a))
    : null;
  const sourceName =
    item?.dataSource?.name ||
    (firstVersion?.changeType === 'created'
      ? firstVersion.createdBy
        ? firstVersion.createdByEmail
        : agentName
      : null);
  const { copy: copyId, state: copyIdState } = useCopy();

  const [isEditing, setIsEditing] = useState(isNew);
  // The full-screen preview of the working copy (UX review v5).
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selected, setSelected] = useState<Record<string, string[]>>({});
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, string>>({});
  const [facts, setFacts] = useState<VerifiedFact[]>([]);
  const [keyPeople, setKeyPeople] = useState<NamedEntity[]>([]);
  const [keyOrganizations, setKeyOrganizations] = useState<NamedEntity[]>([]);
  const [keyLocations, setKeyLocations] = useState<NamedEntity[]>([]);
  const [fields, setFields] = useState<EditableFields>(() =>
    isNew ? { ...emptyFields, contentType: 'article' } : emptyFields
  );
  const [cmsFields, setCmsFields] = useState<CmsFields>(emptyCmsFields);
  // The site's authors, for the byline (Prototype v17).
  const siteAuthors = useSiteAuthors(accountId);
  // What the sidecar held when editing started (or was last saved): the patch base.
  const cmsBaseRef = useRef<CmsFields>(emptyCmsFields);
  // The title/summary/body last sent through the tinycms write (JSON).
  const cmsTextRef = useRef('');
  const [heroPicking, setHeroPicking] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);
  // The summary as typed this edit; null until the person types in it.
  const [summaryTyped, setSummaryTyped] = useState<string | null>(null);
  const [message, setMessage] = useState<string>('');
  // Good news goes to a toast; only problems stay on the page.
  useEffect(() => {
    if (!message || message.startsWith('Failed')) return;
    showInkToast(message);
    setMessage('');
  }, [message]);
  const [expandedVersionId, setExpandedVersionId] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [compareSelecting, setCompareSelecting] = useState(false);
  const [compareSelectedIds, setCompareSelectedIds] = useState<string[]>([]);
  const [compareActive, setCompareActive] = useState(false);
  const [autosaveStatus, setAutosaveStatus] = useState<AutosaveStatus>('idle');
  const [runDraftCopyEdit] = useMutation(RUN_TINYCMS_DRAFT_COPY_EDIT);
  const [draftCheck, setDraftCheck] = useState<{ findings: Finding[]; required?: boolean } | null>(
    null
  );
  // The text the findings on show were read from.
  const [checkedSig, setCheckedSig] = useState('');
  const [bodyEditor, setBodyEditor] = useState<{ editor: Editor | null; locked: boolean }>({
    editor: null,
    locked: false,
  });
  // The text the check's findings were read against. A finding goes from the list
  // when its words were there and now aren't; one the editor never could find
  // stays, because it may still be blocking and the person needs to see it.
  const checkedFrom = useRef<{ doc: PmNode; texts: DraftTexts } | null>(null);
  // Applied: a short quote may still be elsewhere in the text, as other words.
  const [appliedFindings, setAppliedFindings] = useState<Finding[]>([]);
  // `n` remounts the box, so each finding finds its text afresh. A counter of its
  // own: Apply closes the box and opens the next in one update.
  const findingCount = useRef(0);
  const [findingOpen, setFindingOpen] = useState<{ finding: Finding; n: number } | null>(null);
  const skipAutosaveRef = useRef(true);

  const seedFromItem = () => {
    const tax: ProvenanceTaxonomies = item?.taxonomies || {};
    const next: Record<string, string[]> = {};
    for (const [key, terms] of Object.entries(tax)) next[key] = terms.map((t) => t.term);
    skipAutosaveRef.current = true;
    setSelected(next);
    const cf = item?.customFields || {};
    const cfDraft: Record<string, string> = {};
    for (const def of topicFieldDefs) {
      const raw = cf[def.fieldKey];
      if (raw === undefined || raw === null) {
        cfDraft[def.fieldKey] = def.fieldType === 'boolean' ? 'false' : '';
      } else if (def.fieldType === 'json' || typeof raw === 'object') {
        cfDraft[def.fieldKey] = JSON.stringify(raw, null, 2);
      } else if (typeof raw === 'boolean') {
        cfDraft[def.fieldKey] = raw ? 'true' : 'false';
      } else {
        cfDraft[def.fieldKey] = String(raw);
      }
    }
    setCustomFieldValues(cfDraft);
    setFacts((item?.verifiedFacts || []).map((f) => ({ ...f })));
    setKeyPeople((item?.keyPeople || []).map((e) => ({ ...e })));
    setKeyOrganizations((item?.keyOrganizations || []).map((e) => ({ ...e })));
    setKeyLocations((item?.keyLocations || []).map((e) => ({ ...e })));
    setFields({
      title: item?.title || '',
      summary: item?.summary || '',
      bodyText: item?.bodyText || '',
      // Seed from the display pointer — that is what the field now writes.
      // Falls back to media_url for RSS items, which carry a real URL there.
      mediaUrl: item?.primaryMediaUrl || item?.mediaUrl || '',
      destinationUrl: item?.destinationUrl || '',
      category: item?.category || '',
      keywords: item?.keywords || '',
      contentType: item?.contentType || 'webpage',
    });
    const sidecar = cmsFieldsFrom(cms);
    cmsBaseRef.current = sidecar;
    cmsTextRef.current = JSON.stringify({
      title: (item?.title || '').trim(),
      summary: item?.summary || null,
      body: item?.bodyText || null,
    });
    setCmsFields(sidecar);
    setAutosaveStatus('idle');
  };

  const setCmsField = (patch: Partial<CmsFields>) =>
    setCmsFields((prev) => ({ ...prev, ...patch }));
  const cmsDirty =
    !isNew && Boolean(cms) && Object.keys(cmsPatch(cmsBaseRef.current, cmsFields)).length > 0;
  // The new article's slug follows its title until the person changes it.
  const autoSlugRef = useRef(cmsFields.slug);
  useEffect(() => {
    if (!isNew) return;
    const was = autoSlugRef.current;
    const next = slugFromTitle(fields.title);
    autoSlugRef.current = next;
    setCmsFields((prev) => (prev.slug === was ? { ...prev, slug: next } : prev));
  }, [isNew, fields.title]);

  /** The tinycms text fields as they would be sent (empty → null). */
  const cmsText = () => {
    const blank = (v: string) => (!v.trim() || v === '<p></p>' ? null : v);
    return {
      title: fields.title.trim(),
      summary: blank(fields.summary),
      body: blank(fields.bodyText),
    };
  };

  /**
   * Save a tinycms article's title, summary and body (what changed since the
   * last send) through updateTinycmsArticle. An autosave records no version;
   * `final` (Done or Save) also sends the sidecar fields (slug, SEO, alt,
   * author, tags), and `forceVersion` (Save) records a version even when
   * nothing else changed.
   */
  const persistCmsArticle = async ({
    final,
    forceVersion = false,
  }: {
    final: boolean;
    forceVersion?: boolean;
  }) => {
    if (!cms || !accountId || !topicId) return;
    const text = cmsText();
    const textKey = JSON.stringify(text);
    if (!text.title) {
      // The server keeps the old title rather than store an empty one.
      setMessage('Failed: An article needs a title.');
      throw new Error('An article needs a title.');
    }
    const input: Record<string, unknown> = textKey !== cmsTextRef.current ? { ...text } : {};
    const sidecar = final ? cmsPatch(cmsBaseRef.current, cmsFields) : {};
    if (Object.keys(sidecar).length) {
      const problem = cmsFieldsProblem(cmsFields);
      if (problem) {
        setMessage(`Failed: ${problem}`);
        throw new Error(problem);
      }
      Object.assign(input, sidecar);
    }
    const versioned = forceVersion || Object.keys(sidecar).length > 0;
    if (!Object.keys(input).length && !versioned) return;
    try {
      await (versioned ? updateCms : autosaveCms)({
        variables: { accountId, id: topicId, input },
      });
    } catch (err) {
      setMessage(`Failed: ${err instanceof Error ? err.message : 'Could not save'}`);
      throw err;
    }
    cmsTextRef.current = textKey;
    if (final) cmsBaseRef.current = cmsFields;
  };

  // Made in TinyCMS before New article made TinyCMS articles (no feed behind
  // it): it belongs in TinyCMS, so a draft moves over by itself — readers see
  // nothing change. A live one waits for the owner's script
  // (bringAppArticlesIn.js), which records its version 1.
  const autoBrought = useRef(false);
  useEffect(() => {
    if (autoBrought.current || !item || cms || cmsLoading || !cmsData) return;
    if (item.dataSource || item.publishStatus === 'published') return;
    autoBrought.current = true;
    void bringIn({ quiet: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, cms, cmsLoading, cmsData]);

  const refreshAll = () => Promise.all([refetch(), refetchVersions(), refetchCms()]);

  const takeOffline = async () => {
    const wasPublished = item?.publishedDate || null;
    const fromWordPress = String(item?.dataSource?.type || '').toLowerCase() === 'wordpress';
    try {
      await unpublishTopic({ variables: { id: topicId } });
      await refetch();
      showInkToast(
        fromWordPress
          ? 'Unpublished. It comes back if the post changes in WordPress.'
          : 'Unpublished. Readers no longer see it.',
        {
          label: 'Undo',
          ms: 8000,
          onClick: () => {
            republishTopic({ variables: { id: topicId, publishedDate: wasPublished } })
              .then(() => refetch())
              .then(() => showInkToast('It’s live again.'))
              .catch((err) =>
                showInkToast(err instanceof Error ? err.message : 'That couldn’t be undone.')
              );
          },
        }
      );
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work. Try again.');
    }
  };

  const toggleNoindex = async () => {
    if (!cms || !accountId || !topicId) return;
    try {
      await updateCms({
        variables: {
          accountId,
          id: topicId,
          input: { seo: { noindex: !cms.content.seo?.noindex } },
        },
      });
      await refetchVersions();
    } catch (err) {
      setMessage(`Failed: ${err instanceof Error ? err.message : 'Could not save'}`);
    }
  };

  const setField = (key: keyof EditableFields, value: string) =>
    setFields((prev) => ({ ...prev, [key]: value }));

  const [saveContentItem, { loading: saving }] = useMutation(SAVE_CONTENT_ITEM, {
    onError: (e) => setMessage(`Failed: ${e.message}`),
  });
  const [restoreVersion, { loading: restoring }] = useMutation(RESTORE_TOPIC_VERSION, {
    onError: (e) => setMessage(`Failed: ${e.message}`),
  });
  const [atomizeTopic, { loading: atomizing }] = useMutation(ATOMIZE_TOPIC, {
    onError: (e) => setMessage(`Failed: ${e.message}`),
  });
  // Evicting the deleted item keeps every cached list (My Content, topic pickers)
  // in step without refetching them.
  const [deleteArticle, { loading: deleting }] = useMutation(DELETE_TINYCMS_ARTICLE, {
    onError: (e) => setMessage(e.message),
    update: (cache, { data }) => {
      const deletedId = data?.deleteTinycmsArticle?.articleId;
      if (!deletedId) return;
      cache.evict({ id: cache.identify({ __typename: 'Topic', id: deletedId }) });
      cache.gc();
    },
  });
  const [summarizeDraft] = useMutation(SUMMARIZE_CONTENT_DRAFT);
  const publishBusy = deleting;

  // Toolbar "Summarize to bullets": atomize the draft body on the fly and return
  // key-takeaway bullets for the editor to insert.
  const requestSummary = async (plainText: string): Promise<string[]> => {
    const { data } = await summarizeDraft({
      variables: { accountId, title: fields.title, bodyText: plainText },
    });
    return data?.summarizeContentDraft?.bullets ?? [];
  };

  // Seed editable state from the loaded item (not while actively editing —
  // autosave must not reset the cursor via refetch).
  useEffect(() => {
    if (isEditing) return;
    seedFromItem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, cms, isEditing, topicFieldDefs]);

  // Sanitized body HTML for webpage/article content, falling back to the
  // summary/excerpt when no full body was captured.
  // A feed body stored as plain text reads as paragraphs, as on the site (9ae013ed).
  const bodyHtml = useMemo(
    () => toSafeHtml(plainBodyHtml(item?.bodyText || item?.summary || '')),
    [item]
  );
  const summaryHtml = useMemo(() => toSafeHtml(item?.summary), [item]);

  // Which dimensions/values were synced (read-only display).
  const syncedByKey = useMemo(() => {
    const out: Record<string, string[]> = {};
    const tax: ProvenanceTaxonomies = item?.taxonomies || {};
    for (const [key, terms] of Object.entries(tax)) {
      const synced = terms.filter((t) => t.provenance === 'synced').map((t) => t.term);
      if (synced.length) out[key] = synced;
    }
    return out;
  }, [item]);

  const toggleTerm = (key: string, term: string) =>
    setSelected((prev) => {
      const cur = prev[key] || [];
      return {
        ...prev,
        [key]: cur.includes(term) ? cur.filter((t) => t !== term) : [...cur, term],
      };
    });

  const setFreeform = (key: string, value: string) =>
    setSelected((prev) => ({
      ...prev,
      [key]: value
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
    }));

  const buildSaveInput = (): SaveContentItemInput => {
    const taxPayload = Object.entries(selected)
      .map(([key, terms]) => ({ key, terms }))
      .filter((d) => d.terms.length > 0);
    const summaryHtmlValue = fields.summary === '<p></p>' ? '' : toSafeHtml(fields.summary);
    const bodyHtmlValue = fields.bodyText === '<p></p>' ? '' : toSafeHtml(fields.bodyText);
    const customFields: Record<string, unknown> = {};
    for (const def of topicFieldDefs) {
      const raw = customFieldValues[def.fieldKey];
      if (raw === undefined) continue;
      if (def.fieldType === 'json') {
        const trimmed = (raw || '').trim();
        if (!trimmed) {
          customFields[def.fieldKey] = null;
        } else {
          try {
            customFields[def.fieldKey] = JSON.parse(trimmed);
          } catch {
            throw new Error(`Custom field "${def.fieldKey}" must be valid JSON`);
          }
        }
      } else if (def.fieldType === 'boolean') {
        customFields[def.fieldKey] = raw === 'true';
      } else if (def.fieldType === 'number') {
        customFields[def.fieldKey] = raw === '' ? null : Number(raw);
      } else {
        customFields[def.fieldKey] = raw === '' ? null : raw;
      }
    }
    return {
      // A tinycms article's title, summary and body save through the tinycms
      // write (persistCmsArticle), which turns video placeholders into players
      // and checks them; this older write would store them raw.
      ...(cms
        ? {}
        : {
            title: fields.title.trim(),
            summary: summaryHtmlValue,
            bodyText: bodyHtmlValue,
            contentType: fields.contentType || undefined,
          }),
      mediaUrl: fields.mediaUrl.trim(),
      destinationUrl: fields.destinationUrl.trim(),
      category: fields.category.trim(),
      keywords: fields.keywords.trim(),
      taxonomies: taxPayload,
      customFields: topicFieldDefs.length ? customFields : undefined,
      facts: facts.map((f) => ({
        fact: f.fact,
        source_quote: f.source_quote || '',
        attribution: f.attribution || '',
        source_type: f.source_type || '',
        date: f.date || '',
      })),
      keyPeople: keyPeople
        .filter((e) => e.name.trim())
        .map((e) => ({ name: e.name.trim(), role: e.role || '' })),
      keyOrganizations: keyOrganizations
        .filter((e) => e.name.trim())
        .map((e) => ({ name: e.name.trim(), role: e.role || '' })),
      keyLocations: keyLocations
        .filter((e) => e.name.trim())
        .map((e) => ({ name: e.name.trim(), context: e.context || '' })),
    };
  };

  const persistEdit = async (createVersion: boolean) => {
    if (!topicId) return;
    let input: SaveContentItemInput;
    try {
      input = buildSaveInput();
    } catch (err) {
      setMessage(`Failed: ${err instanceof Error ? err.message : 'Invalid custom fields'}`);
      throw err;
    }
    await saveContentItem({
      variables: {
        id: topicId,
        input,
        // A tinycms article's version is recorded by its own write, once.
        createVersion: cms ? false : createVersion,
      },
    });
    if (cms) await persistCmsArticle({ final: createVersion, forceVersion: createVersion });
  };

  const startEdit = () => {
    if (awaitingBringIn) return;
    seedFromItem();
    setSummaryTyped(null);
    setMessage('');
    setIsEditing(true);
    // Allow the next user keystroke to mark the edit unsaved.
    setTimeout(() => {
      skipAutosaveRef.current = false;
    }, 0);
  };

  // Cancel (Prototype v15): nothing here is saved until Save to working copy,
  // so Cancel throws the edits away. One model, not autosave plus a Save button
  // (QA round 7: "Autosave on" beside "Unsaved changes…" and no way to discard).
  const cancelEdit = async () => {
    if (
      (autosaveStatus === 'dirty' || cmsDirty) &&
      !(await askInk({
        title: 'Discard your changes?',
        body: 'The working copy stays as it was.',
        confirmLabel: 'Discard',
        danger: true,
      }))
    ) {
      return;
    }
    seedFromItem();
    setMessage('');
    setIsEditing(false);
    setHeroPicking(false);
    setAutosaveStatus('idle');
  };

  const saveAll = async () => {
    try {
      setAutosaveStatus('saving');
      await persistEdit(true);
      setAutosaveStatus('idle');
      await Promise.all([refetch(), refetchVersions(), refetchCms()]);
      setMessage(
        cms?.state.live
          ? 'Saved to the working copy. Readers still see the live version.'
          : 'Saved to the working copy.'
      );
      setIsEditing(false);
      setHeroPicking(false);
    } catch {
      // onError handlers and persistCmsArticle set the message
      setAutosaveStatus('dirty');
    }
  };

  // New article: Cancel with nothing typed goes back to Articles; with
  // something typed it asks first (the bar turns into the question).
  const apollo = useApolloClient();
  const newTyped = isNew && hasWriting(fields);
  const [discardAsk, setDiscardAsk] = useState(false);
  const [creating, setCreating] = useState(false);
  const leaveNew = (note: string) => {
    navigate('/content?view=articles');
    showInkToast(note);
  };
  const cancelNew = () => {
    if (!newTyped) return leaveNew('Nothing was saved.');
    if (!discardAsk) return setDiscardAsk(true);
    leaveNew('Discarded. Nothing was saved.');
  };

  /** Create draft: the article exists from here on, and its copy edit starts. */
  const createDraft = async () => {
    if (!accountId || creating) return;
    const title = fields.title.trim();
    if (!title) {
      showInkToast('Add a title to create the draft.');
      return;
    }
    // A slug the person typed is kept; else the server picks a free one from the title.
    const autoSlug = slugFromTitle(title);
    const slug = cmsFields.slug && cmsFields.slug !== autoSlug ? cmsFields.slug : null;
    const problem = cmsFieldsProblem({ ...cmsFields, slug: slug || autoSlug });
    if (problem) {
      setMessage(`Failed: ${problem}`);
      return;
    }
    let customFields: Record<string, unknown> | undefined;
    try {
      customFields = topicFieldDefs.length ? buildSaveInput().customFields : undefined;
    } catch (err) {
      setMessage(`Failed: ${err instanceof Error ? err.message : 'Invalid custom fields'}`);
      return;
    }
    const text = cmsText();
    setCreating(true);
    try {
      const { data: made } = await apollo.mutate({
        mutation: CREATE_TINYCMS_ARTICLE,
        variables: {
          accountId,
          input: newArticleInput({
            ...cmsFields,
            title,
            summary: text.summary,
            body: text.body,
            mediaUrl: fields.mediaUrl,
            slug,
            noindex: newNoindex,
            customFields,
          }),
        },
        refetchQueries: ['GetTopicsPage'],
      });
      const id: string | undefined = made?.createTinycmsArticle?.id;
      if (!id) throw new Error('The draft wasn’t created. Try again.');
      // The copy edit runs on its own; the article's checks show it when it's back.
      void apollo
        .mutate({
          mutation: RUN_TINYCMS_COPY_EDIT,
          variables: { accountId, id },
          refetchQueries: ['GetTinycmsArticle'],
        })
        .catch(() => {});
      navigate(`/content/${id}`, { replace: true });
      showInkToast('Draft created. The copy edit and checks are running.');
    } catch (err) {
      setCreating(false);
      setMessage(`Failed: ${err instanceof Error ? err.message : 'The draft wasn’t created'}`);
    }
  };

  // Edits are marked unsaved; nothing reaches the server until Save to working copy.
  useEffect(() => {
    if (!isEditing || !topicId) return;
    if (skipAutosaveRef.current) {
      skipAutosaveRef.current = false;
      return;
    }
    setAutosaveStatus('dirty');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, topicId, fields, selected, facts, keyPeople, keyOrganizations, keyLocations]);

  useEffect(() => {
    if (!isEditing) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (autosaveStatus === 'dirty' || autosaveStatus === 'saving' || cmsDirty || newTyped) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isEditing, autosaveStatus, cmsDirty, newTyped]);

  const autosaveLabel = (() => {
    if (!isEditing) return null;
    if (autosaveStatus === 'saving') return 'Saving…';
    if (autosaveStatus === 'dirty' || cmsDirty) return 'Unsaved changes';
    return null;
  })();

  // ⌘J / Ctrl+J opens and closes "Ask agent" (Prototype v14), on a TinyCMS article.
  const [askOpen, setAskOpen] = useState(false);
  // Opened from the status card's Request changes: the box asks what to change.
  const [askChanges, setAskChanges] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'j') return;
      if (!cms || isEditing || previewOpen) return;
      e.preventDefault();
      setAskChanges(false);
      setAskOpen((v) => !v);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cms, isEditing, previewOpen]);

  // E opens the editor (the review's shortcut), unless typing somewhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== 'e' || e.metaKey || e.ctrlKey || e.altKey) return;
      const el = e.target as HTMLElement | null;
      if (el?.closest('input, textarea, select, [contenteditable="true"]')) return;
      if (isEditing || previewOpen || !item || awaitingBringIn) return;
      e.preventDefault();
      startEdit();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, previewOpen, item, cms, awaitingBringIn]);

  const handleAtomize = async () => {
    if (!topicId) return;
    if (isEditing) {
      setMessage('Failed: Finish or save your edits before extracting facts.');
      return;
    }
    const hasBody = Boolean((item?.bodyText || item?.summary || '').trim());
    if (!hasBody) {
      setMessage('Failed: Add body (or summary) content before extracting facts.');
      return;
    }
    if (
      item?.atomizedAt &&
      !(await askInk({
        title: 'Extract the facts again?',
        body: 'Facts were already extracted from this item. Existing facts, entities, and derived classification will be replaced.',
        confirmLabel: 'Extract again',
      }))
    ) {
      return;
    }
    try {
      setMessage('Extracting facts… this may take a minute.');
      await atomizeTopic({ variables: { topicId } });
      await Promise.all([refetch(), refetchVersions()]);
      setMessage('Done — facts, entities, and classification updated.');
    } catch {
      // onError handlers set the message
    }
  };

  const handleDelete = async () => {
    if (!topicId) return;
    const live = item?.publishStatus && item.publishStatus !== 'draft';
    if (
      !(await askInk({
        title: live ? 'Delete this article?' : 'Delete this draft?',
        body: live ? 'It comes off your site now.' : undefined,
        note: 'You can restore it from Recently deleted for 30 days.',
        confirmLabel: 'Delete',
        danger: true,
      }))
    ) {
      return;
    }
    try {
      await deleteArticle({ variables: { accountId, id: topicId } });
      navigate('/content?view=articles');
    } catch {
      // onError sets the message (e.g. a page still links to it)
    }
  };

  const handleRestore = async (version: TopicVersion) => {
    if (
      !(await askInk({
        title: `Restore version ${version.versionNumber}?`,
        body: 'This replaces the current content and creates a new revision.',
        confirmLabel: 'Restore',
      }))
    ) {
      return;
    }
    try {
      await restoreVersion({
        variables: { topicId, versionId: version.id },
      });
      await Promise.all([refetch(), refetchVersions(), refetchCms()]);
      setMessage(`Restored version ${version.versionNumber}.`);
      setIsEditing(false);
      setExpandedVersionId(null);
    } catch {
      // onError handlers set the message
    }
  };

  const changeTypeLabel = (changeType: string, snapshot?: TopicVersionSnapshot | null) => {
    // The one version a bring-in records (QA round 13).
    if (snapshot?.broughtIn) return 'Brought into tinycms';
    if (changeType === 'created') return 'Created';
    if (changeType === 'restored') return 'Restored';
    return 'Updated';
  };

  const toggleCompareSelect = (versionId: string) => {
    setCompareSelectedIds((prev) => {
      if (prev.includes(versionId)) return prev.filter((id) => id !== versionId);
      if (prev.length >= 3) return prev;
      return [...prev, versionId];
    });
  };

  const startCompareSelect = () => {
    setCompareSelecting(true);
    setCompareActive(false);
    setCompareSelectedIds([]);
    setExpandedVersionId(null);
  };

  const cancelCompareSelect = () => {
    setCompareSelecting(false);
    setCompareActive(false);
    setCompareSelectedIds([]);
  };

  const runCompare = () => {
    if (compareSelectedIds.length < 2) return;
    setCompareActive(true);
  };

  const compareVersions = versions.filter((v) => compareSelectedIds.includes(v.id));

  // Copy edit while editing (backlog f2261d58): the check reads what's on screen,
  // and a finding's row goes to its text. Nothing is saved until Save.
  const draftTexts: DraftTexts = {
    title: fields.title,
    summary: summaryTyped ?? toPlainText(fields.summary, { paragraphs: true }),
    'seo.metaTitle': cmsFields.metaTitle,
    'seo.metaDescription': cmsFields.metaDescription,
  };
  const draftInput = {
    title: fields.title,
    summary: summaryTyped ?? fields.summary,
    body: fields.bodyText,
    metaTitle: cmsFields.metaTitle,
    metaDescription: cmsFields.metaDescription,
  };
  const draftSig = JSON.stringify(draftInput);
  useEffect(() => {
    if (isEditing) {
      setCheckedSig(draftSig);
    } else {
      setDraftCheck(null);
      setFindingOpen(null);
      setAppliedFindings([]);
      checkedFrom.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  const runDraftCheck = async () => {
    const { data } = await runDraftCopyEdit({
      variables: { accountId, id: topicId, draft: draftInput },
    });
    const result = data.runTinycmsDraftCopyEdit;
    setDraftCheck({ findings: result.findings || [], required: result.required });
    setAppliedFindings([]);
    const checkedDoc = bodyEditor.editor?.state.doc;
    checkedFrom.current = checkedDoc ? { doc: checkedDoc, texts: draftTexts } : null;
    setCheckedSig(draftSig);
    if (!result.findings?.length) showInkToast('Copy edit found nothing to change.');
  };

  const stillListed = (f: Finding, doc: PmNode | null) => {
    if (stillInDraft(f, doc, draftTexts)) return true;
    const from = checkedFrom.current;
    return Boolean(from) && !stillInDraft(f, from!.doc, from!.texts);
  };

  const checksWhileEditing = () => {
    if (!cms || !isEditing) return undefined;
    const saved = cms.state.copyEdit;
    const findings = draftCheck?.findings ?? (saved?.fresh ? saved.findings : null);
    const doc = bodyEditor.editor?.state.doc ?? null;
    return {
      state: {
        ...cms.state,
        preflight: (cms.state.preflight || []).filter((f) => stillListed(f, doc)),
        copyEdit: findings
          ? {
              fresh: true,
              // One that was applied or typed over is done with.
              findings: findings.filter(
                (f) => stillListed(f, doc) && !appliedFindings.some((a) => sameFinding(a, f))
              ),
              required: draftCheck?.required ?? saved?.required,
            }
          : saved,
      },
      stale: Boolean(findings) && draftSig !== checkedSig,
      run: runDraftCheck,
    };
  };

  // A finding is applied in Edit, so from reading the click opens it first.
  const openFinding = (finding: Finding) => {
    if (!isEditing) startEdit();
    findingCount.current += 1;
    setFindingOpen({ finding, n: findingCount.current });
  };

  // Apply goes on to the next finding: the list has scrolled out of sight by now.
  // This render's list, which still has the finding just applied to count from.
  const editChecks = checksWhileEditing();
  const afterFinding = (current: Finding) =>
    nextFinding(articleChecks(editChecks?.state || {}).rows, current, (f) =>
      stillInDraft(f, bodyEditor.editor?.state.doc ?? null, draftTexts)
    );
  const openNext = (next: Finding | null) => {
    findingCount.current += 1;
    setFindingOpen(next ? { finding: next, n: findingCount.current } : null);
  };

  const applyToField = (field: CopyEditField, value: string) => {
    if (field === 'title') {
      setField('title', value);
    } else if (field === 'summary') {
      setSummaryTyped(value);
      setField('summary', value);
    } else if (field === 'seo.metaTitle') {
      setCmsField({ metaTitle: value });
    } else {
      setCmsField({ metaDescription: value });
    }
  };

  const renderPrimary = () => {
    if (!item) return null;
    const type = (isEditing ? fields.contentType : item.contentType) || 'webpage';
    // tinycms articles are type "article"; older topics edit as "webpage".
    if (isEditing && (type === 'webpage' || (cms && type === 'article'))) {
      return (
        <ArticleBodyEditor
          value={fields.bodyText}
          onChange={(html) => setField('bodyText', html)}
          accountId={accountId}
          allowVideo={Boolean(cms)}
          storedForm={cms ? undefined : toSafeHtml}
          readOnlyHtml={bodyHtml}
          onRequestSummary={accountId && atomizeOn ? requestSummary : undefined}
          onReady={(editor, locked) => {
            setBodyEditor({ editor, locked });
            if (editor && !checkedFrom.current) {
              checkedFrom.current = { doc: editor.state.doc, texts: draftTexts };
            }
          }}
        />
      );
    }
    if (type === 'video') {
      const src = resolveDisplayMediaUrl(
        (isEditing ? fields.mediaUrl : item.primaryMediaUrl || item.mediaUrl) || ''
      );
      return src ? (
        <video controls className="w-full max-h-[28rem] rounded bg-black" src={src} />
      ) : (
        <p className="text-cmp-ink-2 text-sm">No video URL.</p>
      );
    }
    if (type === 'image') {
      const src = resolveDisplayMediaUrl(
        (isEditing ? fields.mediaUrl : item.primaryMediaUrl || item.mediaUrl) || ''
      );
      return src ? (
        <img
          src={src}
          alt={item.title || ''}
          className="w-full max-h-[28rem] object-contain rounded"
        />
      ) : (
        <p className="text-cmp-ink-2 text-sm">No image URL.</p>
      );
    }
    if (bodyHtml) {
      return (
        <div
          className="ink-detail-body [&_a]:text-cmp-bronze-2 [&_a]:underline [&_a]:underline-offset-2 [&_a:hover]:text-cmp-ink [&_ul]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_h1]:text-xl [&_h1]:font-semibold [&_h1]:mb-2 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:mb-2 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:mb-2 [&_img]:my-3 [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded"
          dangerouslySetInnerHTML={{ __html: bodyHtml }}
        />
      );
    }
    return <p className="text-cmp-ink-2 text-sm">No body content extracted.</p>;
  };

  const heroUrl = resolveDisplayMediaUrl(
    (isEditing ? fields.mediaUrl : item?.primaryMediaUrl || item?.mediaUrl) || ''
  );

  return (
    <div className="ink-split">
      {isEditing && findingOpen ? (
        <CopyEditPopover
          key={findingOpen.n}
          finding={findingOpen.finding}
          editor={bodyEditor.editor}
          bodyLocked={bodyEditor.locked}
          texts={draftTexts}
          onApplyField={applyToField}
          onApplied={() => {
            setAppliedFindings((was) => [...was, findingOpen.finding]);
            openNext(afterFinding(findingOpen.finding));
          }}
          onSkip={(() => {
            const next = afterFinding(findingOpen.finding);
            return next ? () => openNext(next) : undefined;
          })()}
          onClose={() => setFindingOpen(null)}
        />
      ) : null}
      {loading || otherSite || (cmsLoading && !cmsData) || (!item && accountsLoading) ? (
        // Held until the tinycms view is in, so the older controls never flash —
        // and the unavailable page until the sites are in, so it can name the open one.
        <main className="ink-split-main">
          <Loader label="Loading" className="ink-set-empty" />
        </main>
      ) : !item ? (
        // Deleted, another site's, or an unknown id: the server answers empty
        // for all three, so say so instead of loading forever.
        <main className="ink-split-main">
          <ContentUnavailable id={topicId} error={itemError?.message} />
        </main>
      ) : (
        <>
          {previewOpen ? (
            <ArticlePreview
              articleId={item.id}
              title={decode(item.title || 'Untitled')}
              onClose={() => setPreviewOpen(false)}
            />
          ) : null}
          <main className="ink-split-main">
            <div className="ink-detail-bar">
              <button
                type="button"
                className="ink-detail-back"
                onClick={() => navigate('/content')}
              >
                ← Website{item.category ? ` / ${topicLabel(item.category)}` : ''}
              </button>
              <div className="ink-detail-actions">
                {isNew ? (
                  <>
                    <span className="ink-detail-edit-note">
                      {discardAsk
                        ? 'Discard this new article? Nothing has been saved.'
                        : 'New article · nothing is saved until you create the draft'}
                    </span>
                    {discardAsk ? (
                      <button
                        type="button"
                        className="ink-btn"
                        autoFocus
                        onClick={() => setDiscardAsk(false)}
                      >
                        Keep writing
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="ink-btn"
                      data-danger={discardAsk ? 'true' : undefined}
                      onClick={cancelNew}
                      disabled={creating}
                    >
                      {discardAsk ? 'Discard' : 'Cancel'}
                    </button>
                    <button
                      type="button"
                      className="ink-btn ink-btn-primary"
                      onClick={() => void createDraft()}
                      disabled={creating}
                    >
                      {creating ? 'Creating…' : 'Create draft'}
                    </button>
                  </>
                ) : isEditing ? (
                  <>
                    <span className="ink-detail-edit-note">
                      {cms?.state.live
                        ? 'Editing the working copy · live stays unchanged until you publish'
                        : 'Editing the working copy'}
                      {autosaveLabel ? ` · ${autosaveLabel}` : ''}
                    </span>
                    <button
                      type="button"
                      className="ink-btn"
                      onClick={() => void cancelEdit()}
                      disabled={
                        saving ||
                        updatingCms ||
                        restoring ||
                        publishBusy ||
                        autosaveStatus === 'saving'
                      }
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="ink-btn ink-btn-primary"
                      onClick={() => void saveAll()}
                      disabled={
                        saving ||
                        updatingCms ||
                        restoring ||
                        publishBusy ||
                        autosaveStatus === 'saving'
                      }
                    >
                      {saving || updatingCms ? 'Saving…' : 'Save to working copy'}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="ink-btn ink-btn-quiet"
                      onClick={() => setPreviewOpen(true)}
                    >
                      Preview
                    </button>
                    {/* Edit ▾ (designer's notes, 2026-10-25): ask your agent, or
                        edit it yourself. The one way to ask the agent here (UX
                        review round 24 dropped the separate Ask agent button):
                        on a tinycms article it opens the Ask agent box below,
                        as ⌘J does. E still goes straight to the editor. */}
                    {awaitingBringIn ? null : (
                      <AgentFirstButton
                        action="articleEdit"
                        ctx={{ title: item.title }}
                        onSelf={startEdit}
                        onAsk={
                          cms && accountId
                            ? () => {
                                setAskChanges(false);
                                setAskOpen(true);
                              }
                            : undefined
                        }
                        disabled={restoring || publishBusy || atomizing}
                        title="Edit (E)"
                        label={
                          <>
                            Edit
                            <span className="ink-kbd" aria-hidden="true">
                              E
                            </span>
                          </>
                        }
                      />
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Problems show here, under the header's buttons; "Saved…" and other
                good news is a toast (UX review round 6: it sat between Summary and Body). */}
            {message ? (
              <p
                className="ink-rail-note"
                role="alert"
                style={{ color: 'var(--ink-amber)', margin: '0 0 12px' }}
              >
                {message}
              </p>
            ) : null}
            {heroUrl ? (
              <div className="ink-detail-hero" ref={heroRef}>
                {((isEditing ? fields.contentType : item.contentType) || 'webpage') === 'video' ? (
                  <video controls src={heroUrl} />
                ) : (
                  <img
                    src={heroUrl}
                    alt={
                      (isEditing ? cmsFields.heroAlt : cms ? cms.content.hero?.alt : feedHeroAlt) ||
                      ''
                    }
                  />
                )}
                {cms || item.dataSource
                  ? (() => {
                      const alt =
                        (cms
                          ? isEditing
                            ? cmsFields.heroAlt
                            : cms.content.hero?.alt
                          : feedHeroAlt) || '';
                      return (
                        <span className="ink-detail-hero-alt" data-ok={alt ? 'true' : undefined}>
                          {alt ? `Alt: ${alt}` : 'No alt text'}
                          {!alt && cms && !isEditing ? (
                            <>
                              {' · '}
                              <button type="button" onClick={startEdit}>
                                Add
                              </button>
                            </>
                          ) : null}
                        </span>
                      );
                    })()
                  : null}
                {isEditing ? (
                  <span className="ink-detail-hero-tools">
                    <button type="button" onClick={() => setHeroPicking((v) => !v)}>
                      Replace
                    </button>
                    {fields.mediaUrl ? (
                      <button
                        type="button"
                        onClick={() => {
                          setField('mediaUrl', '');
                          setCmsField({ heroAlt: '' });
                        }}
                      >
                        Remove
                      </button>
                    ) : null}
                  </span>
                ) : null}
              </div>
            ) : isEditing ? (
              // No lead image (Prototype v15): one compact line while editing, nothing
              // when reading — not a full-size hatched box (QA round 7).
              <div className="ink-detail-nohero" ref={heroRef}>
                <span>
                  No lead image. Articles without one show text only in lists and link previews.
                </span>
                <button type="button" onClick={() => setHeroPicking((v) => !v)}>
                  Add an image
                </button>
              </div>
            ) : null}
            {isEditing && heroPicking ? (
              <div style={{ marginTop: 12, maxWidth: 760 }}>
                <MediaUrlField
                  accountId={accountId}
                  value={fields.mediaUrl}
                  onChange={(url) => setField('mediaUrl', url)}
                  inputClassName={inputClass}
                  hasAttachedImages={countMediaItems(item.mediaItems, 'images') > 0}
                />
              </div>
            ) : null}
            {isEditing && cms && fields.mediaUrl ? (
              <label className="ink-edit-frame" style={{ marginTop: 12 }}>
                <div className="ink-edit-frame-label">
                  ALT TEXT · read aloud by screen readers · required
                </div>
                <input
                  className="ink-input ink-edit-field"
                  value={cmsFields.heroAlt}
                  onChange={(e) => setCmsField({ heroAlt: e.target.value })}
                  placeholder="Alt text"
                  data-bad={!cmsFields.heroAlt.trim() ? 'true' : undefined}
                />
              </label>
            ) : null}

            {isEditing ? (
              <label className="ink-edit-frame" style={{ marginTop: 24 }}>
                <div className="ink-edit-frame-label">
                  TITLE · headline, page title and link text
                </div>
                <textarea
                  className="ink-input ink-edit-field ink-edit-title"
                  data-ce-field="title"
                  rows={2}
                  value={fields.title}
                  // A title is one line: Enter and pasted line breaks become spaces.
                  onChange={(e) => setField('title', e.target.value.replace(/\s*\n+\s*/g, ' '))}
                  placeholder="Title"
                />
              </label>
            ) : (
              <h1
                className="ink-detail-title ink-detail-editable"
                onDoubleClick={startEdit}
                title="Double-click to edit"
              >
                {decode(item.title || 'Untitled')}
              </h1>
            )}

            <div className="ink-detail-byline">
              {/* The status is the publish panel's to say (backlog b23a8dd3); before
                  it's in tinycms there's no panel. */}
              {cms
                ? null
                : (() => {
                    const st = {
                      label: publishStatusLabel(item.publishStatus),
                      color: statusColor(item.publishStatus),
                    };
                    return (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          fontWeight: 500,
                          color: st.color,
                        }}
                      >
                        <span className="ink-dot" style={{ background: st.color }} />
                        {st.label}
                      </span>
                    );
                  })()}
              {cms ? (
                // The byline as it will publish (Prototype v17), or the way to add one.
                <>
                  <span>
                    <BylineNames
                      ids={isEditing ? cmsFields.authorIds : cms.content.authorIds || []}
                      byId={siteAuthors.byId}
                      legacy={isEditing ? cmsFields.author : cms.content.author}
                      onAdd={isEditing ? undefined : startEdit}
                    />
                  </span>
                  {item.dataSource ? (
                    <>
                      <span>·</span>
                      <span>{sourceLine(item.dataSource)}</span>
                    </>
                  ) : null}
                </>
              ) : item.dataSource ? (
                // The feed's author when it sent one; else where it came from — a
                // feed's name is no byline (QA round 13: "By nasa" became "No author").
                <>
                  <span>·</span>
                  <span>
                    {feedAuthor ? 'By ' : 'From '}
                    <b style={{ fontWeight: 500, color: 'var(--ink-accent)' }}>
                      {feedAuthor || item.dataSource.name}
                    </b>
                  </span>
                </>
              ) : null}
              {item.publishedDate && item.publishStatus !== 'draft' ? (
                <>
                  <span>·</span>
                  <span>
                    {item.publishStatus === 'scheduled' ? 'Goes live ' : 'Published '}
                    {format(new Date(item.publishedDate), 'MMM d, yyyy, h:mm a')}
                  </span>
                </>
              ) : null}
            </div>

            {isEditing && cms ? (
              <BylineEditor
                authors={siteAuthors.list}
                ids={cmsFields.authorIds}
                onChange={(authorIds) => setCmsField({ authorIds })}
                showUpdated={cmsFields.showUpdated}
                onShowUpdated={(showUpdated) => setCmsField({ showUpdated })}
                legacy={cmsFields.author}
                onClearLegacy={() => setCmsField({ author: '' })}
                publishedLine={
                  item.publishedDate && item.publishStatus === 'published'
                    ? `${format(new Date(item.publishedDate), 'MMM d, yyyy · h:mm a')}, when it first went live`
                    : 'Set automatically when you publish'
                }
              />
            ) : null}

            {isEditing ? (
              <div className="ink-edit-frame" style={{ marginTop: 28 }}>
                <div className="ink-edit-frame-label">
                  SUMMARY · used in cards, feeds and LLM context
                </div>
                {/* Plain words (Prototype v15): cards, feeds and LLMs read it as text.
                    Untouched, the stored summary saves exactly as it was. */}
                <textarea
                  className="ink-detail-summary-input"
                  data-ce-field="summary"
                  rows={3}
                  value={summaryTyped ?? toPlainText(fields.summary, { paragraphs: true })}
                  placeholder="Summary"
                  onChange={(e) => {
                    setSummaryTyped(e.target.value);
                    setField('summary', e.target.value);
                  }}
                />
              </div>
            ) : summaryHtml && item.bodyText ? (
              <div className="ink-detail-summary">
                <div dangerouslySetInnerHTML={{ __html: summaryHtml }} />
                <div className="ink-detail-summary-note">
                  SUMMARY · used in cards, feeds and LLM context
                </div>
              </div>
            ) : null}

            <section
              data-part="body"
              className={isEditing ? undefined : 'ink-detail-editable'}
              onDoubleClick={isEditing ? undefined : startEdit}
            >
              <div className="mb-6">{renderPrimary()}</div>
            </section>
            {cms ? (
              <>
                <SearchSharing
                  article={cms}
                  editing={isEditing}
                  draft={cmsFields}
                  onDraft={setCmsField}
                  title={decode((isEditing ? fields.title : item.title) || '')}
                  summary={(isEditing ? fields.summary : item.summary) || ''}
                  heroUrl={heroUrl}
                  siteHost={(siteUrl || '').replace(/^https?:\/\//, '').replace(/\/$/, '')}
                  addressing={articleAddressing}
                  onToggleNoindex={() => (isNew ? setNewNoindex((v) => !v) : void toggleNoindex())}
                  togglingNoindex={updatingCms}
                  structuredData={structuredDataLine(
                    cms.content.authorIds || [],
                    siteAuthors.byId,
                    item.publishStatus === 'published' && item.publishedDate
                      ? new Date(item.publishedDate).toISOString()
                      : null
                  )}
                />
                {isNew ? null : <DatasetCard article={cms} />}
              </>
            ) : null}
            {/* The images panel carries its own "Images" heading. Not before it's
                brought in: the feed's hero shows above, with its alt (QA round 13). */}
            <section data-part="images" className="ink-detail-block" hidden={awaitingBringIn}>
              {accountId && topicId && !awaitingBringIn ? (
                <TopicMediaPanel
                  accountId={accountId}
                  topicId={topicId}
                  // A card leads with its front; an article leads with
                  // a featured image.
                  defaultRole={item.contentType === 'card' ? 'front' : 'featured'}
                  hero={
                    item.primaryMediaUrl || item.mediaUrl
                      ? {
                          url: (item.primaryMediaUrl || item.mediaUrl) as string,
                          alt: cms?.content.hero?.alt || null,
                        }
                      : null
                  }
                  onReplaceHero={() => {
                    if (!isEditing) startEdit();
                    setHeroPicking(true);
                    // After the edit render: with no image, the empty-state row appears only then.
                    window.requestAnimationFrame(() =>
                      heroRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
                    );
                  }}
                  // Saving a hero to the library changes the article's
                  // working copy too, so its approval state is re-read.
                  onChanged={() => void Promise.all([refetch(), refetchCms()])}
                />
              ) : null}
            </section>
            {/* Custom fields (Prototype v16): a label and its value per row; inputs while editing. */}
            <section data-part="fields" className="ink-detail-block">
              <h3>Custom fields</h3>
              {topicFieldDefs.length === 0 ? (
                <p className="ink-cf-empty">
                  This site has no custom fields.{' '}
                  <Link to={ARTICLE_SETTINGS_PATH}>
                    Add fields in Website › Articles › Settings →
                  </Link>
                </p>
              ) : !isEditing ? (
                <>
                  <div className="ink-cf-rows">
                    {topicFieldDefs.map((def: AccountTopicField) => {
                      const value = (customFieldValues[def.fieldKey] ?? '').trim();
                      return (
                        <div key={def.fieldKey} className="ink-cf-row">
                          <span className="ink-cf-key" title={def.description || def.fieldKey}>
                            {def.label}
                          </span>
                          <span className="ink-cf-val" data-empty={value ? undefined : 'true'}>
                            {value || 'Not set'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <p className="ink-cf-note">
                    Extra structured data on each article.{' '}
                    <Link to={ARTICLE_SETTINGS_PATH}>Change the fields →</Link>
                  </p>
                </>
              ) : (
                <div className="space-y-4 max-w-2xl">
                  {topicFieldDefs.map((def: AccountTopicField) => {
                    const value = customFieldValues[def.fieldKey] ?? '';
                    const enumChoices = Array.isArray(def.options) ? def.options.map(String) : [];
                    return (
                      <div key={def.fieldKey}>
                        <p className="text-xs text-cmp-ink mb-1">
                          {def.label}
                          {def.required && (
                            <span className="ml-2 text-[10px] uppercase text-warning">
                              required
                            </span>
                          )}
                          <span className="ml-2 font-mono text-[10px] text-cmp-ink-3">
                            {def.fieldKey}
                          </span>
                        </p>
                        {def.description && (
                          <p className="text-[11px] text-cmp-ink-3 mb-1">{def.description}</p>
                        )}
                        {def.fieldType === 'boolean' ? (
                          <select
                            className={inputClass}
                            value={value || 'false'}
                            onChange={(e) =>
                              setCustomFieldValues((prev) => ({
                                ...prev,
                                [def.fieldKey]: e.target.value,
                              }))
                            }
                          >
                            <option value="true">true</option>
                            <option value="false">false</option>
                          </select>
                        ) : def.fieldType === 'enum' ? (
                          <select
                            className={inputClass}
                            value={value}
                            onChange={(e) =>
                              setCustomFieldValues((prev) => ({
                                ...prev,
                                [def.fieldKey]: e.target.value,
                              }))
                            }
                          >
                            <option value="">—</option>
                            {enumChoices.map((c) => (
                              <option key={c} value={c}>
                                {c}
                              </option>
                            ))}
                          </select>
                        ) : def.fieldType === 'text' || def.fieldType === 'json' ? (
                          <textarea
                            className={`${inputClass} min-h-[120px] font-mono text-xs`}
                            value={value}
                            onChange={(e) =>
                              setCustomFieldValues((prev) => ({
                                ...prev,
                                [def.fieldKey]: e.target.value,
                              }))
                            }
                            placeholder={def.fieldType === 'json' ? '{ ... }' : def.label}
                          />
                        ) : (
                          <Input
                            className={inputClass}
                            type={def.fieldType === 'number' ? 'number' : 'text'}
                            value={value}
                            onChange={(e) =>
                              setCustomFieldValues((prev) => ({
                                ...prev,
                                [def.fieldKey]: e.target.value,
                              }))
                            }
                            placeholder={def.label}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
            {atomizeHeld && seesExtracted && (
              <FactsEntities
                facts={facts}
                people={{ entities: keyPeople, onChange: setKeyPeople }}
                organizations={{ entities: keyOrganizations, onChange: setKeyOrganizations }}
                locations={{ entities: keyLocations, onChange: setKeyLocations }}
                editing={isEditing}
                read={Boolean(item?.atomizedAt)}
                extracting={atomizing}
                onExtract={() => void handleAtomize()}
                sourceTypeLabel={sourceTypeLabel}
                factEditor={
                  <>
                    <div className="flex justify-end mb-3">
                      <Button
                        variant="outline"
                        onClick={() =>
                          setFacts((prev) => [
                            ...prev,
                            {
                              fact: '',
                              attribution: '',
                              source_type: '',
                              date: '',
                              source_quote: '',
                            },
                          ])
                        }
                        className="gap-1"
                      >
                        <Plus className="w-3 h-3" /> Fact
                      </Button>
                    </div>
                    <div className="space-y-3">
                      {facts.map((f, i) => {
                        const patch = (p: Partial<VerifiedFact>) =>
                          setFacts((prev) => prev.map((x, j) => (j === i ? { ...x, ...p } : x)));
                        return (
                          <div key={i} className="border border-cmp-line rounded p-3 space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium text-cmp-ink">#{i + 1}</span>
                              <button
                                onClick={() => setFacts((prev) => prev.filter((_, j) => j !== i))}
                                className="text-error hover:opacity-80"
                                title="Delete fact"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <FieldLabel>Date or time reference</FieldLabel>
                                <Input
                                  value={f.date || ''}
                                  onChange={(e) => patch({ date: e.target.value })}
                                  className={inputClass}
                                  placeholder="Date or time reference"
                                />
                              </div>
                              <div>
                                <FieldLabel>Source type</FieldLabel>
                                <select
                                  value={f.source_type || ''}
                                  onChange={(e) => patch({ source_type: e.target.value })}
                                  className={inputClass}
                                >
                                  <option value="">— Select —</option>
                                  {SOURCE_TYPE_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>
                                      {o.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            <div>
                              <FieldLabel>Attribution</FieldLabel>
                              <Input
                                value={f.attribution || ''}
                                onChange={(e) => patch({ attribution: e.target.value })}
                                className={inputClass}
                                placeholder="Attribution"
                              />
                            </div>
                            <div>
                              <FieldLabel>Verified fact</FieldLabel>
                              <textarea
                                value={f.fact}
                                onChange={(e) => patch({ fact: e.target.value })}
                                rows={2}
                                className={inputClass}
                                placeholder="Verified fact"
                              />
                            </div>
                            <div>
                              <FieldLabel>Original article text</FieldLabel>
                              <textarea
                                value={f.source_quote || ''}
                                onChange={(e) => patch({ source_quote: e.target.value })}
                                rows={2}
                                className={inputClass}
                                placeholder="Original article text"
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                }
              />
            )}
            {askOpen && cms && accountId && !isEditing ? (
              <AskAgent
                key={askChanges ? 'changes' : 'ask'}
                accountId={accountId}
                articleId={item.id}
                agentName={agentName}
                changes={askChanges}
                onClose={() => setAskOpen(false)}
              />
            ) : null}
          </main>
          <aside className="ink-split-aside">
            {isNew ? (
              // Publish, Schedule, the version line and checks come once the draft exists.
              <div className="ink-new-note" data-part="not-saved">
                <span className="ink-new-note-label">Not saved yet</span>
                <p>
                  Create the draft to run the copy edit and checks. Then you can publish or schedule
                  it, or send it to someone to approve.
                </p>
              </div>
            ) : (
              <>
                {cms ? (
                  <>
                    <CmsPublishCard
                      article={cms}
                      editing={isEditing}
                      disabled={restoring || atomizing}
                      onChanged={refreshAll}
                      onRequestChanges={() => {
                        setAskChanges(true);
                        setAskOpen(true);
                      }}
                      onDelete={() => void handleDelete()}
                      deleting={deleting}
                      agentAsk={agentMade ? { name: agentName } : null}
                      onOpenHistory={() => setHistoryOpen(true)}
                    />
                    <ArticleChecks
                      article={cms}
                      editing={isEditing}
                      onChanged={refetchCms}
                      edit={editChecks}
                      onOpenFinding={openFinding}
                    />
                  </>
                ) : (
                  // Not in TinyCMS yet (Prototype, QA round 13): what readers see and
                  // where it came from — no version and no actions until it's
                  // brought in; Bring into TinyCMS below is the way to change it.
                  <div className="ink-rail-card" data-part="status">
                    <div
                      className="ink-rail-status"
                      style={{ color: statusColor(item.publishStatus) }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                        <span
                          className="ink-dot"
                          style={{ background: statusColor(item.publishStatus) }}
                        />
                        {publishStatusLabel(item.publishStatus)}
                      </span>
                      {item.dataSource ? (
                        <span className="ink-rail-kicker ink-mono">from your feed</span>
                      ) : null}
                    </div>
                    <div className="ink-rail-note">
                      {item.publishStatus === 'published'
                        ? `${
                            item.publishedDate
                              ? `Live since ${format(new Date(item.publishedDate), 'MMM d, yyyy')}. `
                              : ''
                          }${
                            !readersSeeRow
                              ? 'Readers don’t see it yet: this site shows only approved articles.'
                              : item.dataSource
                                ? 'It went live from your feed, without an approval here.'
                                : 'It went live before tinycms kept approvals.'
                          }`
                        : item.publishStatus === 'scheduled' && item.publishedDate
                          ? `Goes live ${format(new Date(item.publishedDate), 'MMM d, yyyy, h:mm a')}.`
                          : item.dataSource
                            ? 'A draft from your feed. It isn’t on the site.'
                            : 'This is a draft. It is not on the site.'}
                    </div>
                  </div>
                )}
                {!cms && !cmsLoading ? (
                  // Named by where it came from, not "managed" (Feed Articles design A).
                  <div className="ink-rail-card" data-part="manage">
                    <div className="ink-rail-label">
                      {item.dataSource ? 'From your feed' : 'Made before approvals'}
                    </div>
                    {item.dataSource ? (
                      <div className="ink-rail-note ink-mono" style={{ marginTop: 4 }}>
                        {sourceLine(item.dataSource)}
                      </div>
                    ) : null}
                    <p className="ink-rail-note" style={{ marginTop: 6 }}>
                      {item.dataSource
                        ? 'tinycms shows it as your feed sent it. Bring it in to edit it here, with checks, search and sharing settings and approvals.'
                        : 'It was made here before tinycms kept approvals. Bring it in to edit it with checks, search and sharing settings and approvals.'}
                    </p>
                    <button
                      type="button"
                      className="ink-btn ink-btn-primary"
                      style={{ width: '100%', marginTop: 10 }}
                      disabled={adopting}
                      onClick={() => setBringAsk(true)}
                    >
                      {adopting ? 'Bringing it in…' : 'Bring into tinycms'}
                    </button>
                    {item.publishStatus === 'published' && readersSeeRow ? (
                      <button
                        type="button"
                        className="ink-btn"
                        style={{ width: '100%', marginTop: 8 }}
                        disabled={unpublishing}
                        onClick={() => void takeOffline()}
                      >
                        {unpublishing ? 'Unpublishing…' : 'Unpublish'}
                      </button>
                    ) : null}
                    {(() => {
                      const feed = item.dataSource
                        ? feedData?.feedBringIn?.feeds.find((f) => f.id === item.dataSource!.id)
                        : null;
                      if (
                        !feed ||
                        feed.waiting < 2 ||
                        feedData?.feedBringIn?.job?.status === 'running'
                      )
                        return null;
                      return (
                        <div
                          className="ink-rail-note"
                          style={{ marginTop: 8, textAlign: 'center' }}
                        >
                          {feed.waiting.toLocaleString('en-US')} articles from this feed ·{' '}
                          <Link to="/content?view=articles&bring=all">Bring all in</Link>
                        </div>
                      );
                    })()}
                  </div>
                ) : null}
                {bringAsk ? (
                  <div
                    className="ink-modal-back"
                    onMouseDown={(e) => {
                      if (e.target === e.currentTarget && !adopting) setBringAsk(false);
                    }}
                  >
                    <div
                      className="ink-modal"
                      role="dialog"
                      aria-modal="true"
                      aria-labelledby="bring-in-title"
                    >
                      <div className="ink-modal-title" id="bring-in-title">
                        {item.publishStatus === 'published' && readersSeeRow
                          ? 'Bring this article into tinycms?'
                          : 'Bring this draft into tinycms?'}
                      </div>
                      <div className="ink-modal-copy">
                        {item.publishStatus === 'published' && readersSeeRow
                          ? item.dataSource
                            ? // It comes in with its feed's author and tags, which then show.
                              feedAuthor
                              ? `It stays live as version 1, recorded as approved by you, and shows the author (${feedAuthor}) and tags your feed gave it. From now on you edit it here, and nothing goes live until you approve it.`
                              : 'It stays live as version 1, recorded as approved by you, with the tags your feed gave it. Your feed didn’t name an author, so add one once it’s in. From now on you edit it here, and nothing goes live until you approve it.'
                            : 'It stays live, unchanged, as version 1, recorded as approved by you. From now on you edit it here, and nothing goes live until you approve it.'
                          : item.publishStatus === 'published'
                            ? // This site shows only approved versions, so readers don't see it yet.
                              'Readers don’t see it yet: this site shows only approved articles. It comes in as a draft, and goes live when you approve it.'
                            : 'It stays a draft. You edit it here, and it goes live when you approve it.'}
                      </div>
                      {item.dataSource ? (
                        <div className="ink-modal-note">{bringOneNote(item.dataSource.url)}</div>
                      ) : null}
                      <div className="ink-modal-actions">
                        <button
                          type="button"
                          className="ink-btn"
                          autoFocus
                          disabled={adopting}
                          onClick={() => setBringAsk(false)}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="ink-btn ink-btn-primary"
                          disabled={adopting}
                          onClick={() => void bringIn()}
                        >
                          {adopting ? 'Bringing it in…' : 'Bring into tinycms'}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}
                {accountId && item.duplicates?.length ? (
                  <DuplicateCard
                    accountId={accountId}
                    articleId={item.id}
                    duplicates={item.duplicates}
                    onChanged={refreshAll}
                  />
                ) : null}
                <section data-part="classification" className="ink-rail-block">
                  <div className="ink-rail-label">Classification</div>
                  <dl className="ink-rail-meta">
                    <dt>Topic</dt>
                    <dd>
                      {isEditing ? (
                        <Input
                          value={fields.category}
                          onChange={(e) => setField('category', e.target.value)}
                          placeholder="Topic"
                          className={inputClass}
                        />
                      ) : (
                        topicLabel(item.category) || '—'
                      )}
                    </dd>
                    {cms ? (
                      // With none, the byline's "+ Add author" says so; Checks warns once.
                      !(isEditing ? cmsFields.authorIds : cms.content.authorIds || []).length &&
                      !(isEditing ? cmsFields.author : cms.content.author) ? null : (
                        <>
                          <dt>Author</dt>
                          <dd>
                            {/* Set in the Byline while editing (Prototype v17). */}
                            <BylineNames
                              prefix={false}
                              ids={isEditing ? cmsFields.authorIds : cms.content.authorIds || []}
                              byId={siteAuthors.byId}
                              legacy={isEditing ? cmsFields.author : cms.content.author}
                            />
                          </dd>
                        </>
                      )
                    ) : (
                      <>
                        <dt>Author</dt>
                        <dd className="ink-muted">
                          {/* The byline is a TinyCMS field: say where it comes from (QA round 12). */}
                          Credit an author once it’s in tinycms.
                        </dd>
                      </>
                    )}
                    <dt>Tags</dt>
                    <dd>
                      {cms ? (
                        isEditing ? (
                          <TagEditor
                            tags={cmsFields.tags}
                            onChange={(tags) => setCmsField({ tags })}
                          />
                        ) : (
                          <span className="ink-rail-terms">
                            {cms.content.tags.map((term) => (
                              <span className="ink-rail-chip" key={term}>
                                {term}
                              </span>
                            ))}
                            {!cms.content.tags.length ? '—' : null}
                          </span>
                        )
                      ) : isEditing ? (
                        <Input
                          value={fields.keywords}
                          onChange={(e) => setField('keywords', e.target.value)}
                          placeholder="Keywords, separated by commas"
                          className={inputClass}
                        />
                      ) : (
                        <span className="ink-rail-terms">
                          {(item.keywords || '')
                            .split(',')
                            .map((term) => term.trim())
                            .filter(Boolean)
                            .map((term) => (
                              <span className="ink-rail-chip" key={term}>
                                {term}
                              </span>
                            ))}
                          {!(item.keywords || '').trim() ? '—' : null}
                        </span>
                      )}
                    </dd>
                    {isEditing && !cms ? (
                      <>
                        <dt>Type</dt>
                        <dd style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {(['webpage', 'video', 'image'] as const).map((type) => (
                            <button
                              key={type}
                              type="button"
                              onClick={() => setField('contentType', type)}
                              className="ink-btn ink-btn-quiet"
                              style={
                                fields.contentType === type
                                  ? { background: 'var(--ink-bg3)', color: 'var(--ink-ink)' }
                                  : undefined
                              }
                            >
                              {type.charAt(0).toUpperCase() + type.slice(1)}
                            </button>
                          ))}
                        </dd>
                      </>
                    ) : null}
                    {/* Prism AI's taxonomy, apart from the article's own Topic and Tags so the
                    two sets don't read as duplicates (UX review round 6). TinyCMS technical
                    support only, like Facts & entities (design decision 2026-10-21). */}
                    {seesExtracted ? (
                      <>
                        {dimensions.some((dim) => isEditing || (selected[dim.key] || []).length) ||
                        Object.keys(syncedByKey).some(
                          (key) => !dimensions.some((d) => d.key === key)
                        ) ? (
                          <dt className="ink-rail-subhead">Prism AI taxonomy</dt>
                        ) : null}
                        {dimensions.map((dim) => {
                          const values = selected[dim.key] || [];
                          if (!isEditing && values.length === 0) return null;
                          return (
                            <span key={dim.key} style={{ display: 'contents' }}>
                              <dt>{dimensionLabel(dim.key)}</dt>
                              <dd>
                                {!isEditing ? (
                                  <span className="ink-rail-terms">
                                    {values.map((term) => (
                                      <span className="ink-rail-chip" key={term}>
                                        {term}
                                      </span>
                                    ))}
                                  </span>
                                ) : dim.isFreeform || dim.terms.length === 0 ? (
                                  <Input
                                    placeholder="Values, separated by commas"
                                    value={values.join(', ')}
                                    onChange={(e) => setFreeform(dim.key, e.target.value)}
                                    className={inputClass}
                                  />
                                ) : (
                                  <span className="ink-rail-terms">
                                    {dim.terms.map((term) => {
                                      const on = values.includes(term);
                                      return (
                                        <button
                                          key={term}
                                          type="button"
                                          data-on={on ? 'true' : 'false'}
                                          className="ink-rail-chip"
                                          onClick={() => toggleTerm(dim.key, term)}
                                        >
                                          {term}
                                        </button>
                                      );
                                    })}
                                  </span>
                                )}
                              </dd>
                            </span>
                          );
                        })}
                        {Object.entries(syncedByKey)
                          .filter(([key]) => !dimensions.some((dim) => dim.key === key))
                          .map(([key, terms]) => (
                            <span key={key} style={{ display: 'contents' }}>
                              <dt>{dimensionLabel(key)}</dt>
                              <dd>
                                <span className="ink-rail-terms">
                                  {terms.map((term) => (
                                    <span className="ink-rail-chip" key={term}>
                                      {term}
                                    </span>
                                  ))}
                                </span>
                              </dd>
                            </span>
                          ))}
                      </>
                    ) : null}
                  </dl>
                </section>
                {atomizeOn && seesExtracted && (
                  <div className="ink-rail-block">
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                      }}
                    >
                      <div className="ink-rail-label" style={{ margin: 0 }}>
                        Prism AI
                      </div>
                    </div>
                    <div className="ink-rail-note">
                      {/* Say what's actually there: an extraction can find nothing (UX review round 6). */}
                      {item.atomizedAt
                        ? facts.length
                          ? `${facts.length} ${facts.length === 1 ? 'fact' : 'facts'} extracted ${format(new Date(item.atomizedAt), 'MMM d, yyyy')}.`
                          : `Checked ${format(new Date(item.atomizedAt), 'MMM d, yyyy')}: no facts found in the article.`
                        : 'No facts yet. Extract facts and entities from the article.'}
                    </div>
                    <button
                      type="button"
                      className="ink-btn"
                      style={{ width: '100%', marginTop: 12 }}
                      onClick={() => void handleAtomize()}
                      disabled={atomizing || publishBusy || restoring}
                    >
                      {atomizing ? 'Extracting facts…' : 'Extract facts & entities'}
                    </button>
                  </div>
                )}
                <ArticleReachCard
                  reachOn={reachOn}
                  article={{
                    id: item.id,
                    title: item.title || '',
                    summary: item.summary,
                    imageUrl:
                      item.contentType === 'video'
                        ? null
                        : resolveDisplayMediaUrl(item.primaryMediaUrl || item.mediaUrl || '') ||
                          null,
                    publishStatus: item.publishStatus,
                    publishedDate: item.publishedDate,
                  }}
                />
                {awaitingBringIn ? <WriteKeyHistory versions={versions} /> : null}
                {/* No history before it's in TinyCMS (Prototype, QA round 13): it starts at
                "Brought into TinyCMS · v1 approved", after where it came from. */}
                <section data-part="history" className="ink-rail-block" hidden={awaitingBringIn}>
                  {cms ? null : (
                    <>
                      <h3 className="ink-rail-label">History</h3>
                      <div className="ink-rail-kicker" style={{ margin: '0 0 10px' }}>
                        SAVED VERSIONS
                      </div>
                      {compareActive && compareVersions.length >= 2 ? (
                        <VersionCompareView
                          articleTags={Boolean(cms)}
                          versions={compareVersions}
                          onClose={() => {
                            setCompareActive(false);
                            setCompareSelecting(true);
                          }}
                        />
                      ) : (
                        <>
                          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                            <p className="text-xs text-cmp-ink-3 max-w-xl">
                              Full snapshots from create and each save. Restore replaces the current
                              record and adds a new revision.
                              {compareSelecting && (
                                <span className="block mt-1 text-cmp-ink">
                                  Select 2–3 versions to compare side by side.
                                  {compareSelectedIds.length >= 3 ? ' Maximum of 3 selected.' : ''}
                                </span>
                              )}
                            </p>
                            <div className="flex flex-wrap items-center gap-2">
                              {compareSelecting ? (
                                <>
                                  <Button
                                    variant="outline"
                                    className="h-8 text-xs"
                                    onClick={cancelCompareSelect}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    className="h-8 text-xs gap-1.5"
                                    disabled={compareSelectedIds.length < 2}
                                    onClick={runCompare}
                                  >
                                    <Columns2 className="size-3.5" />
                                    Compare
                                    {compareSelectedIds.length > 0
                                      ? ` (${compareSelectedIds.length})`
                                      : ''}
                                  </Button>
                                </>
                              ) : (
                                <Button
                                  variant="outline"
                                  className="h-8 text-xs gap-1.5"
                                  disabled={versions.length < 2}
                                  onClick={startCompareSelect}
                                >
                                  <Columns2 className="size-3.5" />
                                  Compare
                                </Button>
                              )}
                            </div>
                          </div>
                          {versionsLoading ? (
                            <Loader label="Loading history" className="text-sm text-cmp-ink-2" />
                          ) : versions.length === 0 ? (
                            <p className="text-sm text-cmp-ink-2">
                              No versions yet. Save an edit to start tracking revisions.
                            </p>
                          ) : (
                            <div className="space-y-2">
                              {versions.map((v) => {
                                const snap = v.snapshot || {};
                                const open = expandedVersionId === v.id;
                                const taxKeys = snap.taxonomies
                                  ? Object.keys(snap.taxonomies).length
                                  : 0;
                                const factCount = snap.atoms?.verified_facts?.length ?? 0;
                                const checked = compareSelectedIds.includes(v.id);
                                const atMax = compareSelectedIds.length >= 3 && !checked;
                                return (
                                  <div
                                    key={v.id}
                                    className={`border rounded p-3 space-y-2 ${
                                      checked ? 'border-primary bg-primary/5' : 'border-cmp-line'
                                    }`}
                                  >
                                    <div className="flex items-start justify-between gap-3">
                                      <div className="flex items-start gap-3 flex-1 min-w-0">
                                        {compareSelecting && (
                                          <div className="pt-1 flex-shrink-0">
                                            <Checkbox
                                              checked={checked}
                                              onCheckedChange={() => toggleCompareSelect(v.id)}
                                              className={
                                                atMax
                                                  ? 'opacity-40 pointer-events-none'
                                                  : 'border-cmp-line-2'
                                              }
                                            />
                                          </div>
                                        )}
                                        <button
                                          type="button"
                                          className="text-left flex-1 min-w-0"
                                          onClick={() => {
                                            if (compareSelecting) {
                                              if (!atMax || checked) {
                                                toggleCompareSelect(v.id);
                                              }
                                              return;
                                            }
                                            setExpandedVersionId(open ? null : v.id);
                                          }}
                                        >
                                          <div className="flex flex-wrap items-center gap-2">
                                            <span className="text-sm text-cmp-ink font-medium">
                                              Version {v.versionNumber}
                                            </span>
                                            <Badge variant="outline" className="text-cmp-ink">
                                              {changeTypeLabel(v.changeType, v.snapshot)}
                                            </Badge>
                                          </div>
                                          <p className="text-xs text-cmp-ink-2 mt-1">
                                            {format(new Date(v.createdAt), 'PPpp')}
                                            {v.createdByEmail ? ` · ${v.createdByEmail}` : ''}
                                          </p>
                                          <p className="text-sm text-cmp-ink mt-1 truncate">
                                            {snap.title || 'Untitled'}
                                          </p>
                                        </button>
                                      </div>
                                      {!compareSelecting && (
                                        <Button
                                          variant="outline"
                                          className="flex-shrink-0 h-8 text-xs"
                                          disabled={restoring}
                                          onClick={() => handleRestore(v)}
                                        >
                                          {restoring ? 'Restoring…' : 'Restore'}
                                        </Button>
                                      )}
                                    </div>
                                    {open && !compareSelecting && (
                                      <div className="pt-2 border-t border-cmp-line space-y-2 text-xs text-cmp-ink">
                                        <p>
                                          <span className="text-cmp-ink-3">Type: </span>
                                          <span className="capitalize">
                                            {snap.contentType || '—'}
                                          </span>
                                        </p>
                                        <p>
                                          <span className="text-cmp-ink-3">Taxonomy dims: </span>
                                          {taxKeys}
                                          {seesExtracted ? (
                                            <>
                                              <span className="text-cmp-ink-3"> · Facts: </span>
                                              {factCount}
                                            </>
                                          ) : null}
                                        </p>
                                        {snap.summary && (
                                          <div>
                                            <span className="text-cmp-ink-3 block mb-1">
                                              Summary
                                            </span>
                                            <div
                                              className="line-clamp-4 [&_p]:mb-1"
                                              dangerouslySetInnerHTML={{
                                                __html: toSafeHtml(snap.summary),
                                              }}
                                            />
                                          </div>
                                        )}
                                        {snap.bodyText && (
                                          <div>
                                            <span className="text-cmp-ink-3 block mb-1">Body</span>
                                            <div
                                              className="line-clamp-6 [&_p]:mb-1"
                                              dangerouslySetInnerHTML={{
                                                __html: toSafeHtml(snap.bodyText),
                                              }}
                                            />
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}
                </section>
                <div className="ink-rail-foot">
                  {/* Shortened as the design shows it; a click copies the whole ID. */}
                  <button
                    type="button"
                    className="ink-rail-id"
                    title={`${item.id} · Click to copy`}
                    onClick={(e) => void copyId('id', item.id, e)}
                  >
                    ID {item.id.slice(0, 8)}…{item.id.slice(-4)}
                    {copyIdState('id') === 'copied'
                      ? ' · Copied'
                      : copyIdState('id') === 'failed'
                        ? ' · Couldn’t copy'
                        : ''}
                  </button>
                  {/* A feed's name is under the title already. */}
                  {sourceName && !(cms && item.dataSource) ? ` · Source: ${sourceName}` : ''}
                </div>
              </>
            )}
          </aside>
        </>
      )}
      {historyOpen && cms ? (
        <HistoryView
          kind="article"
          id={cms.id}
          // Where it came from is History's oldest entry (backlog b23a8dd3).
          origin={
            item?.dataSource
              ? { source: sourceLine(item.dataSource), at: item.publishedDate || null }
              : null
          }
          onClose={() => setHistoryOpen(false)}
          onChanged={refreshAll}
          onOpenChecks={() => {
            setHistoryOpen(false);
            document
              .querySelector('[data-part="checks"]')
              ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }}
        />
      ) : null}
    </div>
  );
}

const TINYCMS_LIVE_SOURCE = gql`
  query TinycmsLiveSource($accountId: ID!) {
    tinycmsSettings(accountId: $accountId) {
      liveSource
    }
  }
`;

/** "ktnewsroom.com/feed · RSS": the feed an article came from, as design A names it. */
function sourceLine(source: { name: string; type?: string | null }): string {
  const kind = String(source.type || '').toLowerCase();
  const label =
    kind === 'rss'
      ? 'RSS'
      : kind === 'wordpress'
        ? 'WordPress'
        : kind
          ? kind[0].toUpperCase() + kind.slice(1)
          : '';
  return label ? `${source.name} · ${label}` : source.name;
}
